"use client";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Stack, Typography } from "@mui/material";

export type PasswordRequirementKey =
  | "length"
  | "uppercase"
  | "lowercase"
  | "number"
  | "symbol";

/** Fixed render order so the checklist is stable as the user types. */
const REQUIREMENT_ORDER: PasswordRequirementKey[] = [
  "length",
  "uppercase",
  "lowercase",
  "number",
  "symbol",
];

const DEFAULT_LABELS: Record<PasswordRequirementKey, string> = {
  length: "At least 10 characters",
  uppercase: "An uppercase letter",
  lowercase: "A lowercase letter",
  number: "A number",
  symbol: "A special character",
};

/**
 * Evaluates each requirement against `value`. The character-class checks mirror
 * the rules used by `check-password-strength` (which backs `PasswordStrengthMeter`),
 * so this checklist stays consistent with the strength meter.
 */
export const evaluatePasswordRequirements = (
  value: string,
  minLength = 10
): Record<PasswordRequirementKey, boolean> => ({
  length: value.length >= minLength,
  uppercase: /[A-Z]/.test(value),
  lowercase: /[a-z]/.test(value),
  number: /[0-9]/.test(value),
  symbol: /[^a-zA-Z0-9]/.test(value),
});

export interface PasswordRequirementsProps {
  value: string;
  /** Minimum length used for the `length` check. Defaults to 10. */
  minLength?: number;
  /** Overrides visible requirement labels. Defaults to English. */
  labels?: Partial<Record<PasswordRequirementKey, string>>;
  /** Optional heading shown above the checklist. */
  title?: string;
  /** Appended to each item's accessible label when satisfied. Defaults to English. */
  metLabel?: string;
  /** Appended to each item's accessible label when not satisfied. Defaults to English. */
  unmetLabel?: string;
  dataCy?: string;
  className?: string;
}

export const PasswordRequirements = ({
  value,
  minLength = 10,
  labels,
  title,
  metLabel = "met",
  unmetLabel = "not met",
  dataCy = "password_requirements",
  className,
}: PasswordRequirementsProps) => {
  const met = evaluatePasswordRequirements(value, minLength);
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };

  return (
    <Stack spacing={1} className={className} data-cy={dataCy}>
      {title && (
        <Typography variant="inputLabelMd" color="text.secondary">
          {title}
        </Typography>
      )}
      <Stack
        component="ul"
        spacing={1}
        sx={{ listStyle: "none", margin: 0, padding: 0 }}
      >
        {REQUIREMENT_ORDER.map((key) => {
          const isMet = met[key];
          const Icon = isMet
            ? CheckCircleRoundedIcon
            : RadioButtonUncheckedRoundedIcon;
          return (
            <Stack
              key={key}
              component="li"
              direction="row"
              spacing={1}
              alignItems="center"
              aria-label={`${resolvedLabels[key]}: ${
                isMet ? metLabel : unmetLabel
              }`}
              data-cy={`${dataCy}__item_${key}`}
            >
              <Icon
                fontSize="small"
                aria-hidden
                sx={(theme) => ({
                  color: isMet
                    ? theme.vars!.palette.rmh.icon.success
                    : theme.vars!.palette.rmh.icon.secondary,
                })}
              />
              <Typography
                variant="bodySmRegular"
                color={isMet ? "text.primary" : "text.secondary"}
              >
                {resolvedLabels[key]}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
